import React from 'react';
import classNames from 'classnames';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { useViewTracking } from '@reverbdotcom/commons/src/use_tracking';
import { trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import Location from '../lib/wrapped_location';
import { useApplePay } from './apple_pay_hook';
import experiments from '@reverbdotcom/commons/src/experiments.json';
import { formatExperimentsForEventAttributes } from '@reverbdotcom/commons/src/elog/mparticle';

interface Props {
  listingCurrencyCode: string;
  paymentMethodsConfig: string;
  payButtonType?: 'checkout' | 'plain';
  buttonLocation?: 'hoisted' | 'default';
}

ApplePayButton.defaultProps = {
  payButtonType: 'plain',
};

export function ApplePayButton(props: Props) {
  const user = useUser();

  const { openPaymentSheet, isAvailable, paymentResult, loading } = useApplePay({
    paymentMethodsConfig: props.paymentMethodsConfig,
    listingCurrencyCode: props.listingCurrencyCode,
  });

  useApplePayOfferedTracking({ loading, isAvailable });

  const classes = classNames(
    'apple-pay-button',
    { 'apple-pay-button--checkout': props.payButtonType === 'checkout' },
  );

  if (!isAvailable) {
    return null;
  }

  if (paymentResult.paymentResultUrl) {
    Location.assign(paymentResult.paymentResultUrl);
  }

  const openPaymentSheetWithTracking = buildTrackAndOpenPaymentSheet({ openPaymentSheet, user, buttonLocation: props.buttonLocation });

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={openPaymentSheetWithTracking}
      onKeyPress={withKeyPressHandling(openPaymentSheetWithTracking)}
      className={classes}
      lang={user.locale}
    >
    </div>
  );
}

function useApplePayOfferedTracking({ loading, isAvailable }) {
  useViewTracking({
    componentName: 'ApplePayButton',
    eventName: MParticleEventName.ApplePayOffered,
    applePayAvailableForDevice: isAvailable,
  }, !loading);
}

function buildTrackAndOpenPaymentSheet({ openPaymentSheet, user, buttonLocation }) {
  return function trackAndOpenPaymentSheet(event) {
    trackEvent({
      eventName: MParticleEventName.ClickedApplePayButton,
      experiments: formatExperimentsForEventAttributes(user, [experiments.HOIST_APPLE_PAY]),
      displayStyle: buttonLocation === 'hoisted' ? 'hoisted' : 'default',
    });

    openPaymentSheet(event);
  };
}

function withKeyPressHandling(handler) {
  return function callHandlerOnKeyPress(event) {
    if (['Enter', ' '].includes(event.key)) {
      handler(event);
    }
  };
}
